import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createApi, IS_DEMO } from './api'
import { onMaintenanceDetected } from './api/maintenance'
import { actingAs, setActingAs } from './api/act-as'
import { onEntryRefused } from './api/entry'
import { ApiError, NOTHING_CLOSED, entryRefused, mayUseDeveloperMode, type ApiFailure, type MaintenanceNotice, type Me } from './api/types'
import { describeAuthError, getApiToken, initAuth, signIn, signOut } from './auth/msal'
import { REPORT_PAGES, Shell, type PageId } from './components/Shell'
import { useDurableGridLayouts } from './components/gridLayoutStore'
import { applySettingsFromMe } from './components/rememberedCurrency'
import { FailureState } from './components/FailureState'
import { PlatformClosed, ReadGraceBanner, ReportClosed } from './components/MaintenanceScreen'
import { AccessDenied } from './components/AccessDenied'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Button, Loading } from './components/ui'
import { BrandMark, Wordmark } from './components/Brand'
import { LoginAsPicker } from './components/LoginAs'
import { NewsPage } from './pages/NewsPage'
import { ReceivablesAgingPage } from './pages/ReceivablesAgingPage'
import { SalesPage } from './pages/SalesPage'
import { MaterialPlanningPage } from './pages/MaterialPlanningPage'
import { InventoryAgingPage } from './pages/InventoryAgingPage'
import { StatementPage } from './pages/StatementPage'
import { MaintenancePage } from './pages/MaintenancePage'
import { PermissionsPage } from './pages/PermissionsPage'
import { AccessStaleStrip } from './pages/AccessStaleStrip'

type Phase = 'starting' | 'signed-out' | 'ready'

export default function App() {
  const api = useMemo(() => createApi(getApiToken), [])
  const [phase, setPhase] = useState<Phase>('starting')
  /* ONE IDENTITY NOW, AND THAT IS THE WHOLE CHANGE.
     ------------------------------------------------------------------------
     Under View As these were two different people: `me` stayed the
     administrator while `surface` carried the target's roles and reports, and
     keeping them apart was load-bearing because collapsing them printed the
     real name beside the borrowed status — "demo.user@redachem.com — No
     platform role" — on the one screen whose job is saying whose eyes you are
     looking through.

     Login As removes the question. Both are the SESSION, which is the person
     being acted as, so the footer shows their name AND their status and the two
     agree. `surface` is the fresher of the two and is what the menu reads;
     `me` is the answer from startup.

     The truth lives in `surface.actor`, which names whoever is really at the
     keyboard, and is rendered in violet above every screen. See `realActor`. */
  const [me, setMe] = useState<Me | null>(null)
  const [surface, setSurface] = useState<Me | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  // The account Microsoft signed in but the API turned down: one from a company
  // MBI does not accept. Shown on the sign-in screen, which otherwise came back
  // with no word of why.
  const [refusedAccount, setRefusedAccount] = useState<string | null>(null)
  // Null until the surface says where this person can land, and null FOREVER
  // if the answer is nowhere. See firstReachablePage.
  const [page, setPage] = useState<PageId | null>(null)

  /* THE ACTING SESSION, held as an OBJECT ID and mirrored in sessionStorage.
     ------------------------------------------------------------------------
     It lives here rather than in a page because the banner has to appear above
     every screen, so the state has to be above every screen.

     The id — not the address — because that is what the server takes, and
     because sec.Principal_MBI.Email declares no collation, so routing the choice
     through an address would invent a case-sensitivity question.

     It SURVIVES A RELOAD, which is the opposite of what View As did. Acting is
     a deliberate act with a permanent banner; losing it to a stray F5 halfway
     through checking what somebody sees was an annoyance with no safety
     benefit. It still dies with the tab — see api/act-as.ts. */
  const [actingOid, setActingOidState] = useState<string | null>(() => actingAs())
  const [pickerOpen, setPickerOpen] = useState(false)
  // Where to land once a Login As session has ended: "Stop, and open Access" on
  // the Access denied screen. Applied when /me answers as the administrator again,
  // because until then the refused person's identity can reach none of it.
  const afterActing = useRef<PageId | null>(null)
  // "See Mindzer sync" on the stale-data strip: a request Access uses once.
  const [syncRequest, setSyncRequest] = useState(0)
  const syncOpened = useCallback(() => setSyncRequest(0), [])

  /* Start or stop acting. The module write happens FIRST so that the header is
     already in place for whatever request the re-render triggers — setting
     React state first would leave a window in which the app believes it is
     somebody else and the fetch wrapper does not agree. */
  const setActing = useCallback((oid: string | null) => {
    setActingAs(oid)
    setActingOidState(oid)
  }, [])

  /* A WINDOW THAT OPENED WHILE SOMEBODY WAS ALREADY WORKING.
     ------------------------------------------------------------------------
     `surface.maintenance` answers "what was closed when this tab last asked".
     This answers the other half: an operator opens a window at 11:04 and the
     people already signed in learn about it from the next request that is
     refused.

     Without it the feature would work perfectly for anyone who arrives after
     the window opens and not at all for anyone interrupted by it — which is
     the group an operator is most likely to be interrupting.

     Deliberately does NOT trigger an immediate re-read of /me. That route
     answers from a 10-second cache, so a read fired the instant a 503 arrives
     can still return "nothing closed" and flick the screen back open, into a
     report that immediately fails again. The closed screen starts its own
     30-second recheck; by the time it runs, the cache has caught up. */
  const [lateNotice, setLateNotice] = useState<MaintenanceNotice | null>(null)
  useEffect(() => onMaintenanceDetected(setLateNotice), [])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      // STARTUP MUST ALWAYS REACH A PHASE.
      //
      // initAuth() can throw — misconfiguration, a blocked third-party-cookie
      // policy, a failed redirect handshake. Until 4 Aug 2026 nothing caught it,
      // so the promise rejected, no setPhase ever ran, and the app sat on
      // "Starting MBI…" for ever. A Loading that never resolves is the
      // worst failure mode available: it looks like slowness, so people wait
      // instead of reporting it, and the console message that would have
      // explained it is never read.
      let account: Awaited<ReturnType<typeof initAuth>> = null
      try {
        account = await initAuth()
      } catch (e: unknown) {
        if (cancelled) return
        // describeAuthError puts the ACTION first and Microsoft's text second.
        // Every failure that reaches here is a setting on the app registration,
        // and the raw AADSTS message reads like a fault in the platform — which
        // sends whoever is looking at it to the wrong place.
        setFailure({ kind: 'config', message: describeAuthError(e) })
        setPhase('ready')
        return
      }
      if (cancelled) return

      if (!account && !IS_DEMO) {
        setPhase('signed-out')
        return
      }

      // Identity is resolved from the API, not from the token. The token says
      // who signed in; only the platform can say what they are allowed to do,
      // and a role claim in a JWT survives revocation until it expires.
      try {
        // No viewAs here: this is the signed-in identity, resolved once.
        const identity = await api.me()
        if (cancelled) return
        // BEFORE the surface: the reports mount on it, and they open in the
        // currency this stores. See components/rememberedCurrency.ts.
        applySettingsFromMe(identity)
        setMe(identity)
        setSurface(identity)
        setPhase('ready')
      } catch (e: unknown) {
        if (cancelled) return
        if (e instanceof ApiError && e.failure.kind === 'unauthenticated') {
          if (e.failure.refused && account) setRefusedAccount(account.username)
          setPhase('signed-out')
          return
        }
        setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not reach the platform.' })
        setPhase('ready')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [api])

  /* RE-READ IDENTITY WHEN THE ACTING SESSION CHANGES.
     ------------------------------------------------------------------------
     There is no surface to borrow any more. `api.me()` takes no argument and
     never did anything but ask "who am I" — under Login As the answer simply IS
     the person being acted as, because the header set in the fetch wrapper made
     the substitution before the request reached a handler.

     So this effect no longer merges two identities. It re-asks the one question
     whenever the acting session changes, because every answer the app is
     holding — roles, reports, scope, maintenance — belongs to whoever it was
     before.

     Separate from the startup effect so switching identity does not re-run the
     auth handshake. On failure it keeps what it had rather than blanking the
     navigation: the menu is not a control, and emptying it over a transient
     error would strand an administrator in their own platform. */
  useEffect(() => {
    if (!me) return
    let cancelled = false
    api
      .me()
      .then((s) => {
        if (cancelled) return
        // The (possibly new) identity's remembered currency, stored before the
        // surface changes, so a report that sees the new identity reads it.
        applySettingsFromMe(s)
        setSurface(s)

        /* THE SAFEGUARD FOR A PERSISTED SESSION, and it belongs HERE rather
           than in an effect watching `surface`.
           --------------------------------------------------------------
           If this tab thinks it is acting as somebody and the server did not
           name an actor, the substitution is not happening: the target was
           deactivated, or the capability was revoked. Drop the local state
           rather than leave a banner asserting an identity nobody is applying.

           Written the obvious way first — an effect on [actingOid, surface] —
           and that was a bug the demo build caught in about a minute. Choosing
           somebody sets actingOid, React re-renders while `surface` is STILL
           the previous answer with no actor, and the effect cancelled the
           session before the re-read could return. The picker closed and
           nothing happened.

           This response was fetched WITH the current header, so it is the only
           answer that can honestly be compared against it. */
        if (actingOid && !s.actor) setActing(null)
        if (!actingOid && afterActing.current) {
          if (canReach(afterActing.current, s)) setPage(afterActing.current)
          afterActing.current = null
        }
      })
      .catch(() => !cancelled && setSurface(me))
    return () => {
      cancelled = true
    }
  }, [api, me, actingOid, setActing])

  /* LEAVE A PAGE THE BORROWED IDENTITY CANNOT REACH.
     ------------------------------------------------------------------------
     Hiding the sidebar entry is not enough on its own. An administrator sitting
     on Access who switches into View As as a salesperson would keep the Access
     screen on screen with no menu entry pointing at it — a page the navigation
     says does not exist, still rendering. Worse, the screen would be showing
     REAL administrative data under a banner announcing somebody else's
     permissions, which is precisely the confusion View As exists to prevent.

     Not a security control: the endpoints behind those screens authorise the
     real caller and always did. This is about the screen agreeing with the menu. */
  useEffect(() => {
    if (!surface) return
    if (page && canReach(page, surface)) return

    // Land on the first page this identity can actually open. Until 6 Aug 2026
    // this fell back to the catalogue, which everybody could reach; with that
    // page gone the fallback has to be derived, and it is allowed to be null.
    setPage(firstReachablePage(surface))
  }, [surface, page])

  /* IS IT OVER YET?
     ------------------------------------------------------------------------
     Re-reads the identity, which is what carries maintenance state. Called by
     every closed screen on a 30-second timer and by its Check again button, so
     a window lifted at 14:05 lets people back in at 14:05 without anybody
     thinking to press reload.

     Re-entrancy guard rather than a debounce: a page can fire six report calls
     at once, and six refusals must not become six identity reads. Dropping the
     extras is right — they would all return the same answer. */
  const rechecking = useRef(false)
  const recheck = useCallback(() => {
    if (rechecking.current) return
    rechecking.current = true
    // Whose session this asked about. An answer that arrives after Login As was
    // stopped or switched belongs to somebody else, and must not redraw the screen
    // as them (review, 27 Oct 2026: a late recheck put "Access denied" back up
    // after Stop sign in as, with both Stop buttons then doing nothing).
    const askedAs = actingAs()

    api
      .me()
      .then((fresh) => {
        if (actingAs() !== askedAs) return
        // Cleared on every SUCCESSFUL read, whatever it says. `fresh` is
        // authoritative and carries the window if it is still open, so keeping
        // the late notice as well would mean a lifted window could only be
        // cleared by a reload.
        setLateNotice(null)
        applySettingsFromMe(fresh)
        setSurface(fresh)
        // Under View As the signed-in identity is not what was read — `fresh`
        // carries the TARGET's roles and reports. Writing it to `me` would put
        // the borrowed status beside the real name in the sidebar footer, the
        // one sentence the two-identity split exists to prevent.
        if (!actingOid) setMe(fresh)
      })
      .catch(() => {
        /* Still closed, or still unreachable. Either way the screen already
           says so, and replacing a maintenance page with an error page would
           be a worse answer to the same question. */
      })
      .finally(() => {
        rechecking.current = false
      })
  }, [api, actingOid])

  // Refused at the door mid-session (an approval taken back while they work):
  // re-read /me, and the whole screen becomes "Access denied" (api/entry.ts).
  useEffect(() => onEntryRefused(recheck), [recheck])

  /* WHAT IS CLOSED, FOR THIS IDENTITY, RIGHT NOW.
     ------------------------------------------------------------------------
     `bypass` is the server's own answer to "is this caller served anyway" —
     the same test MaintenanceMiddleware applies, reported rather than
     re-derived here. Deriving it from roles in the browser is how the two drift
     apart, and the drift that matters is the one where the client draws a
     closed sign in front of the System Admin who has to lift the window. */
  /* THE ADDRESS THE SCREEN SHOWS, derived from what the SERVER said.
     ------------------------------------------------------------------------
     `surface.user` is whoever this session is, and `surface.actor` is non-null
     only while that is somebody else. So this is the borrowed address, and it
     is null the moment the server stops honouring the substitution.

     That last part is the safeguard the persisted session needs. The tab can
     hold an oid the server has stopped accepting — the target deactivated, the
     capability revoked — and a banner that kept claiming an identity nobody is
     applying would be worse than no banner. Deriving it from the response
     rather than from local state means the two cannot disagree. */
  const actingEmail = surface?.actor ? surface.user : null

  /* EVERY READER'S OWN COLUMN LAYOUTS, loaded once and saved as they change.
     No user interface: a reader who never thinks about it finds their columns
     where they left them instead of losing them on every reload.

     Mounted here because it is above every report, and keyed on WHOEVER THE
     SESSION CURRENTLY IS -- `surface?.user` is the person being acted as under
     Login As. Changing it reloads from scratch and empties what is in memory,
     because the layout store is module-level and would otherwise carry an
     administrator's own arrangement into somebody else's screen and save it
     there. Failure is silent by design; see the hook. */
  // Not for a person refused at the door: their layouts are behind it too.
  useDurableGridLayouts(api, surface && !entryRefused(surface) ? surface.user : null)

  /* WHO IS REALLY AT THE KEYBOARD, wherever that has to be printed.
     ------------------------------------------------------------------------
     `me` is no longer reliably the administrator, and that is deliberate rather
     than a regression. On a reload the acting session is already in place, so
     the very first /me is an acting read and `me` becomes the person being
     acted as. The sidebar footer therefore shows THEM — name, roles and scope
     count together.

     That is the coherent answer under full substitution, and it is better than
     what View As did. The bug the two-identity split was built to prevent was a
     MIXED footer: the real name beside the borrowed status,
     "demo.user@redachem.com — No platform role". Here name and status agree,
     and the violet banner above states the truth on every screen.

     But the printed attribution is a different question. Salesperson Review
     prints "by {actor}" into a document somebody keeps, and that line must name
     the human, not the identity they were wearing. */
  const realActor = surface?.actor?.email ?? me?.user ?? null

  const closed = surface?.maintenance ?? NOTHING_CLOSED
  const platformNotice = closed.bypass
    ? null
    : closed.platform ?? (lateNotice?.scope === 'platform' ? lateNotice : null)

  const reportNotice = (slug: string): MaintenanceNotice | null =>
    closed.bypass
      ? null
      : closed.reports.find((r) => r.slug === slug) ?? (lateNotice?.slug === slug ? lateNotice : null)

  /* Read grace is the operator's explicit "keep serving reads", and the API is
     then genuinely answering. Replacing the screen would contradict a server
     that is working, so a grace window annotates instead of closing. */
  const blocking = (n: MaintenanceNotice | null) => (n && !n.allowReadGrace ? n : null)

  if (phase === 'starting') {
    return (
      <div className="grid h-full place-items-center bg-(--color-surface-2)">
        <div className="flex flex-col items-center gap-4">
          <Loading size="lg" label="Starting MBI…" />
        </div>
      </div>
    )
  }

  if (phase === 'signed-out') return <SignIn refusedAccount={refusedAccount} />

  /* NOT LET IN, SO NOTHING IS DRAWN (05 7.9).
     ------------------------------------------------------------------------
     Before the Shell and before maintenance, as PlatformClosed is drawn: a
     person the API refuses at the door would otherwise get a menu of doors that
     will not open. The API is what keeps them out (its entry gate answers only
     /me); this is the screen agreeing with it. New model only: under legacy /me
     sends no entry and this never draws.

     Under Login As an administrator sees exactly this, as the person would,
     plus why and the way back out. */
  if (surface && entryRefused(surface)) {
    return (
      <AccessDenied
        user={realActor}
        reference={surface.correlationId}
        why={surface.entry?.adminExplanation ?? surface.entry?.adminReason ?? null}
        phone={!!surface.entry?.allowed && surface.entry.phone === 'refused'}
        viewAs={actingEmail}
        onRecheck={recheck}
        onSignOut={() => void signOut()}
        onExitViewAs={() => setActing(null)}
        onOpenAccess={() => {
          afterActing.current = 'permissions'
          setActing(null)
        }}
      />
    )
  }

  /* THE PLATFORM IS CLOSED, SO THE PLATFORM IS NOT DRAWN.
     ------------------------------------------------------------------------
     Before the Shell, deliberately. The old behaviour signed the user in, built
     the sidebar, offered every report they were entitled to and refused each
     one as they opened it — a building with the lights on and a note taped
     inside every office. Nobody read that as "closed"; they read it as broken.

     This is not what stops them reading data. MaintenanceMiddleware refuses
     every report route regardless, and deleting this branch in a debugger
     produces an empty shell full of 503s rather than access. It is what stops
     the platform pretending to be open.

     System Admins never reach here — `closed.bypass` is true for them, so the
     one person who can lift the window is never shut out by it. Under View As
     they DO reach here, on purpose, because availability is part of what the
     borrowed identity experiences; the screen carries its own way back out. */
  if (platformNotice && !platformNotice.allowReadGrace) {
    return (
      <PlatformClosed
        notice={platformNotice}
        user={realActor}
        onRecheck={recheck}
        onSignOut={() => void signOut()}
        viewAs={actingEmail}
        onExitViewAs={() => setActing(null)}
      />
    )
  }

  // The report being looked at, if the current page is one.
  const here = REPORT_PAGES.find((r) => r.page === page)
  const hereNotice = here ? reportNotice(here.slug) : null
  const hereClosed = blocking(hereNotice)

  // Somewhere else to go. Decides one sentence on the closed screen, and it has
  // to be true: "your other reports are unaffected" in front of somebody whose
  // only report is this one is a smaller lie than the old panel, but the same
  // kind.
  const otherReportsOpen = (surface?.reports ?? []).some(
    (slug) => slug !== here?.slug && !blocking(reportNotice(slug)),
  )

  // Under read grace the report still renders; this says why it might move.
  const graceNotice = platformNotice ?? (hereNotice && hereNotice.allowReadGrace ? hereNotice : null)

  // The stale-data strip (05 section 7.2): System Admins, the new model, never while acting.
  const staleStrip = me?.permissionsModel === 'v2' && !actingEmail && (surface?.roles ?? []).includes('SystemAdmin')

  return (
    <>
      <Shell
        /* The sidebar marks what is closed BEFORE it is clicked. Not a control
           and not a substitute for the screen — a courtesy, in the same spirit
           as hiding a report the identity cannot open. A grace window is
           deliberately not marked: that report works. */
        closedReports={(surface?.reports ?? []).filter((slug) => blocking(reportNotice(slug)))}
        notice={
          graceNotice || staleStrip ? (
            <>
              {graceNotice && <ReadGraceBanner notice={graceNotice} reportName={here?.name ?? 'This report'} />}
              {staleStrip && (
                <AccessStaleStrip
                  api={api}
                  onOpenSync={() => {
                    setSyncRequest((n) => n + 1)
                    setPage('permissions')
                  }}
                />
              )}
            </>
          ) : null
        }
        me={me}
        surface={surface}
        page={page}
        onNavigate={setPage}
        onSignOut={() => void signOut()}
        viewAs={actingEmail}
        onOpenViewAs={() => setPickerOpen(true)}
        onExitViewAs={() => setActing(null)}
      >
        {/* Per-page net: a crash in one report is contained here — the sidebar
            and other tabs survive; resetKeys clears it the moment the user
            navigates elsewhere OR enters/leaves View As, either of which makes
            the failed render worth retrying. */}
        <ErrorBoundary variant="page" resetKeys={[page, actingOid]}>
          {failure ? (
            <FailureState failure={failure} />
          ) : hereClosed ? (
            /* THE REPORT IS NOT RENDERED AT ALL.
               Not mounted-and-empty, not mounted-behind-a-panel: the component
               never runs. Its tabs, filter bar and KPI strip would otherwise
               draw, fire their own requests, collect their own refusals and
               show four loading states that resolve to four error panels — the
               door-by-door refusal again, inside a single screen. */
            <ReportClosed
              notice={hereClosed}
              reportName={here?.name ?? 'This report'}
              otherReportsOpen={otherReportsOpen}
              onRecheck={recheck}
            />
          ) : page === 'news' ? (
            <NewsPage api={api} roles={surface?.roles ?? []} viewAs={actingEmail} onNavigate={setPage} />
          ) : page === 'recv-aging' ? (
            <ReceivablesAgingPage api={api} viewAs={actingEmail} roles={surface?.roles ?? []} actor={realActor} />
          ) : page === 'sales' ? (
            <SalesPage api={api} viewAs={actingEmail} roles={surface?.roles ?? []} />
          ) : page === 'material-planning' ? (
            <MaterialPlanningPage api={api} viewAs={actingEmail} roles={surface?.roles ?? []} />
          ) : page === 'inventory-aging' ? (
            <InventoryAgingPage api={api} viewAs={actingEmail} roles={surface?.roles ?? []} />
          ) : page === 'statement' ? (
            <StatementPage api={api} viewAs={actingEmail} roles={surface?.roles ?? []} />
          ) : page === 'maintenance' ? (
            <MaintenancePage api={api} />
          ) : page === 'permissions' ? (
            <PermissionsPage
              api={api}
              roles={surface?.roles ?? []}
              actor={realActor}
              account={surface?.user ?? null}
              isV2={me?.permissionsModel === 'v2'}
              openSync={syncRequest}
              onSyncOpened={syncOpened}
              onLoginAs={
                me?.canViewAsData
                  ? (oid) => {
                      // As the picker: the audited switch is done, so act, and land on News.
                      setActing(oid)
                      setPage('news')
                    }
                  : undefined
              }
            />
          ) : null}
        </ErrorBoundary>
      </Shell>

      {pickerOpen && (
        <LoginAsPicker
          api={api}
          current={actingOid}
          onPick={(target) => {
            setActing(target.oid)
            setPickerOpen(false)
            // Land on News, which everybody can reach. Under full substitution
            // the previous choice — jumping to Receivables — could land on a
            // report the person being acted as cannot open, so the first thing
            // an administrator saw of the feature was a refusal.
            setPage('news')
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  )
}

/**
 * THE SIGN-IN SCREEN.
 *
 * A BRAND MOMENT, NOT A BRIEFING. Rewritten 4 Aug 2026.
 *
 * The previous version named the customer and listed how the platform works —
 * what it filters, what it audits, what it refuses. Both were wrong here:
 *
 *   * NO CUSTOMER NAME. The same build has to stand up in front of the next
 *     client, and a screen that says who it was made for cannot.
 *   * NO SYSTEM DETAIL. Telling a signed-OUT visitor what the platform contains
 *     and how its permissions behave is free reconnaissance, and it is dull.
 *     Everything on this screen is now visible to anyone who loads the URL, so
 *     nothing on it should be worth knowing.
 *
 * What is left is a promise and one button. The motion carries the rest: the
 * mark draws itself, the line arrives, the rule sweeps under it. Confidence
 * shown rather than claimed.
 */
/**
 * Can this identity open this page?
 *
 * One list, used for both "should I stay here" and "where should I land",
 * because two copies of that rule would eventually disagree and the disagreement
 * would show up as a page you can navigate to but get bounced out of.
 *
 * Kept in step with NAV in Shell.tsx. It is not a control — every endpoint
 * behind these screens authorises the real caller independently.
 */
function canReach(page: PageId, surface: Me): boolean {
  switch (page) {
    // Open to everybody. It is the landing page, and the reason nobody signs in
    // to an empty frame — see NewsPage.
    case 'news':
      return true
    case 'recv-aging':
      return surface.reports.includes('receivables')
    case 'sales':
      return surface.reports.includes('sales')
    case 'material-planning':
      return surface.reports.includes('material-planning')
    case 'inventory-aging':
      return surface.reports.includes('inventory-aging')
    case 'statement':
      return surface.reports.includes('statement-of-account')
    case 'maintenance':
      return surface.roles.includes('SystemAdmin')
    case 'permissions':
      // System Development reaches it too, for the Developer mode tab alone (D5).
      return mayUseDeveloperMode(surface.roles)
  }
}

/**
 * Where this person lands.
 *
 * Always News: it is the platform's front page and the one screen with no gate
 * on it, so the answer is never "nowhere". The signature stays nullable because
 * `page` is null before the surface has been read, not because this can fail.
 */
function firstReachablePage(_surface: Me): PageId {
  return 'news'
}

/* ON A PHONE THE BRAND PANEL COMES BACK. Rebuilt 29 Sep 2026.
 *
 * Below 1024px the navy panel used to be `hidden`, which left a phone with a
 * white card on a grey field and nothing else: the one screen everybody sees
 * first was the only one that looked unfinished. Now the whole phone is the
 * navy panel — the same ledger grid and slow glows, the mark at the top, the
 * headline — and the sign-in rises from the bottom edge as a white sheet,
 * where a thumb reaches the button. A phone held sideways puts the two side by
 * side, as a laptop does, because a stacked sheet does not fit 390px of height.
 *
 * EVERY PHONE CLASS CARRIES `max-lg:`, so at 1024px and above the markup
 * computes exactly what it did before; the laptop screens were captured
 * before and after and compared byte for byte. `max-lg:` is a viewport
 * variant, and this file is one of the two the container-query gate allows,
 * because the sign-in screen has no content pane: the window is all there is. */
function SignIn({ refusedAccount }: { refusedAccount: string | null }) {
  return (
    /* `signin-root` is only a hook for one phone rule in styles.css: the page
       behind this screen turns navy and stops stretching, so pulling it down
       on an iPhone shows no grey strip above the panel. */
    <div className="signin-root grid min-h-full lg:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)] max-lg:flex max-lg:flex-col max-lg:bg-(--color-navy-800) max-lg:[@media(max-height:500px)]:flex-row">
      {/* ================= Brand panel ================= */}
      <div className="on-navy relative hidden flex-col justify-between overflow-hidden bg-(--color-navy-800) px-14 py-14 text-white lg:flex max-lg:flex max-lg:flex-1 max-lg:gap-10 max-lg:px-7 max-lg:pb-16 max-lg:pt-9 max-lg:[@media(max-height:500px)]:gap-4 max-lg:[@media(max-height:500px)]:px-10 max-lg:[@media(max-height:500px)]:py-8">
        <SignInBackdrop />

        <div className="relative anim-rise flex items-center gap-3">
          <BrandMark size={38} tone="white" animate />
          <Wordmark tone="white" subtitle="" />
        </div>

        <div className="relative max-w-xl">
          <h1
            className="anim-rise text-[46px] font-semibold leading-[1.06] tracking-[-0.03em] text-white max-lg:text-[40px] max-lg:[@media(max-height:500px)]:text-[34px]"
            style={{ animationDelay: '120ms' }}
          >
            {/* Set slightly proud of the second line — a hanging indent, on
                purpose. The two lines are not equals: this one is the claim and
                "in numbers." qualifies it, so the emphasis already carried by
                full-strength white is reinforced by the line starting first.

                4px at 46px. Enough to read as intentional, small enough that
                nobody mistakes it for a broken margin.

                inline-block because a transform does nothing to an inline box.
                The transform is on the SPAN, not the h1 — the h1 is running
                anim-rise, and a transform here would be overwritten by it. */}
            <span className="inline-block -translate-x-[4px]">Your vision,</span>
            <br />
            <span className="text-white/55">in numbers.</span>
          </h1>

          {/* The rule sweeps out from under the headline. One gesture, 900ms,
              and it never repeats — it belongs to the arrival, not the page. */}
          <div
            aria-hidden
            className="anim-draw mt-7 h-[3px] w-28 rounded-full bg-white/70 max-lg:mt-6 max-lg:w-20 max-lg:[@media(max-height:500px)]:mt-4"
            style={{ animationDelay: '420ms' }}
          />

          {/* Short on purpose. The first version explained a thesis across two
              clauses and a dash; nobody reads a paragraph on a sign-in screen.
              Two beats that finish the headline's thought. */}
          <p
            className="anim-rise mt-7 text-[19px] font-light leading-snug tracking-[-0.01em] text-white/70 max-lg:mt-5 max-lg:text-[17px] max-lg:[@media(max-height:500px)]:mt-3"
            style={{ animationDelay: '260ms' }}
          >
            See clearly. Decide with confidence.
          </p>
        </div>

        {/* On a phone the maker's name moves into the sheet, under the button:
            the bottom of this panel is behind the sheet there. */}
        <p
          className="anim-rise relative text-[11px] tracking-[0.12em] text-white/30 max-lg:hidden"
          style={{ animationDelay: '520ms' }}
        >
          MINDZER SYSTEMS
        </p>
      </div>

      {/* ================= Action panel =================
          The panel is TINTED and the card is white. On a white-on-white panel a
          card has no edge to show, which is why the content was floating before:
          it was a card, it just had nothing to sit against. */}
      {/* On a phone this panel IS the sheet: white, rounded at the top, lifted
          over the navy by a shadow cast upward, and pulled 28px into the panel
          above so the headline sits on navy and the sheet visibly rises out of
          it. The bottom padding clears the iPhone's home indicator. Sideways,
          it becomes a column at the right, rounded on its inner edge. */}
      <div className="relative flex items-center justify-center bg-(--color-surface-2) px-6 py-16 max-lg:z-10 max-lg:-mt-7 max-lg:rounded-t-[28px] max-lg:bg-(--color-surface) max-lg:pb-[max(1.75rem,env(safe-area-inset-bottom))] max-lg:pt-3 max-lg:shadow-[0_-18px_44px_-18px_rgb(15_23_42/0.55)] max-lg:[@media(max-height:500px)]:mt-0 max-lg:[@media(max-height:500px)]:w-[min(26rem,48%)] max-lg:[@media(max-height:500px)]:rounded-none max-lg:[@media(max-height:500px)]:rounded-l-[28px] max-lg:[@media(max-height:500px)]:py-6">
        {/* The faintest wash of brand, so the panel is not a flat grey slab.
            Not on a phone, where the panel is a white sheet on navy. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-(--color-navy-50) via-transparent to-transparent max-lg:hidden"
        />

        <div
          className="anim-rise relative w-full max-w-[400px]"
          style={{ animationDelay: '180ms' }}
        >
          {/* The card's chrome is laptop furniture. On a phone the sheet is
              already the surface, so a bordered card inside it would be a box
              in a box: border, shadow, band and the repeated mark all go, and
              the words and the button stay. */}
          <div className="overflow-hidden rounded-xl border border-(--color-line) bg-(--color-surface) shadow-(--shadow-raised) max-lg:overflow-visible max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
            {/* A 3px band of brand across the top. It gives the card a "front"
                and stops a plain rectangle reading as a modal. */}
            <div
              aria-hidden
              className="h-[3px] w-full bg-gradient-to-r from-(--color-navy-700) via-(--color-navy-500) to-(--color-navy-300) max-lg:hidden"
            />

            <div className="px-9 pb-9 pt-8 text-center max-lg:px-1 max-lg:pb-5 max-lg:pt-6 max-lg:[@media(max-height:500px)]:py-2">
              {/* The mark anchors the card and gives the eye somewhere to land
                  before the heading. It draws itself on arrival. On a phone
                  the navy panel above already carries it. */}
              <div className="flex justify-center max-lg:hidden">
                <BrandMark size={46} animate />
              </div>

              <h2 className="mt-6 text-(length:--text-2xl) font-semibold tracking-[-0.02em] text-(--color-ink) max-lg:mt-0">
                Welcome back
              </h2>
              <p className="mx-auto mt-2 max-w-[290px] text-(length:--text-base) leading-relaxed text-(--color-ink-3)">
                Sign in with your work account to continue.
              </p>

              {/* Microsoft signs in work accounts from any company; the API takes
                  only the companies it lists, and its own accounts, never a guest,
                  and says no more than 401. A 401 can also be MBI's own fault (a
                  setting missing after a deploy), so the words blame nobody. The
                  button below asks which account, so another can be chosen. */}
              {refusedAccount && (
                <p role="alert" className="mx-auto mt-4 max-w-[320px] rounded-md border border-l-[3px] border-(--color-line) border-l-(--color-warn) bg-(--color-surface-3) px-3 py-2 text-left text-(length:--text-body) leading-snug text-(--color-ink-2)">
                  MBI could not sign you in as <span className="font-medium break-all">{refusedAccount}</span>. Use your own company work account. If that is the one you used, tell your MBI administrator.
                </p>
              )}

              <div className="mt-7 max-lg:mt-6">
                <Button variant="primary" size="lg" full onClick={() => void signIn()}>
                  <MicrosoftGlyph />
                  Continue with Microsoft
                </Button>
              </div>
            </div>
          </div>

          {/* Under the button, and quiet: the maker's name, which on a laptop
              sits at the foot of the navy panel. Never shown at 1024px and up. */}
          <p className="mt-2 text-center text-[11px] tracking-[0.12em] text-(--color-ink-4) lg:hidden">
            MINDZER SYSTEMS
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * The ambient backdrop: a faint ledger grid, two slow-drifting glows, and a
 * horizon wash.
 *
 * Slow on purpose — 26 and 34 seconds, out of phase. Anything faster becomes
 * something you watch instead of something you feel, and this is a screen people
 * pass through every morning.
 */
function SignInBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />
      <div className="anim-drift absolute -right-32 -top-40 h-[520px] w-[520px] rounded-full bg-(--color-navy-500)/35 blur-[110px]" />
      <div
        className="anim-drift absolute -bottom-48 -left-28 h-[460px] w-[460px] rounded-full bg-(--color-navy-400)/20 blur-[120px]"
        style={{ animationDuration: '34s', animationDirection: 'reverse' }}
      />
      {/* Grounds the panel so the glows do not float free of it. */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-(--color-navy-900)/60 to-transparent" />
    </div>
  )
}


/** The Microsoft squares. Four rects, drawn rather than fetched. */
function MicrosoftGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden className="shrink-0">
      <rect x="0" y="0" width="7" height="7" fill="#F25022" />
      <rect x="9" y="0" width="7" height="7" fill="#7FBA00" />
      <rect x="0" y="9" width="7" height="7" fill="#00A4EF" />
      <rect x="9" y="9" width="7" height="7" fill="#FFB900" />
    </svg>
  )
}
